import {
  callSchema,
  interactionAggregateRecordSchema,
  type ConversationFacts,
  type InteractionIntent,
  messageSchema,
  patientSchema,
  threadSchema,
} from './conversations-contracts'
import { cleanText, phoneNumber } from '../shared/patient-normalization'

export type ConversationChannel = 'SMS' | 'Voice' | 'Web Widget' | 'Unknown'
export type ConversationFilter = 'All' | 'SMS' | 'Voice' | 'Web Widget'
export type AttentionFilter =
  'Everything' | 'Needs Action' | 'Needs Review' | 'Unread' | 'Starred'
export type IntentFilter =
  | 'Any open request'
  | 'Appointment requests'
  | 'Callback requests'
  | 'Cancellation requests'
  | 'Reschedule requests'
export type ConversationStateFilter = 'Active' | 'Reviewed'
export type MessageAuthor =
  'Patient' | 'AI' | 'Staff' | 'Clinic' | 'Outbound unknown' | 'Unknown'

export type PatientDetails = {
  displayName: string
  associatedPatientNames?: string[]
  phone: string | null
  dateOfBirth: string | null
  email: string | null
  insurance: string | null
  procedure: string | null
  patientStatus: string | null
  source: string | null
  aiMode: boolean | null
  favorite: boolean
  isEmergency: boolean
  appointmentBooked: boolean
  recordedRequests: Array<
    'Appointment' | 'Callback' | 'Cancellation' | 'Reschedule'
  >
}

/**
 * Frontend-only view of the current patient-keyed inbox record. This is not the
 * future canonical cross-channel Interaction Aggregate and must never be used
 * to imply a PMS patient or merged Voice/SMS identity.
 */
export type LegacyInteractionAggregate = {
  identityBasis: 'LEGACY_PATIENT_KEY'
  conversationId: string
  aggregateId: null
  patientId: string
  timestamp: number
  preview: string
  channel: ConversationChannel
  lastMessageCallId: string | null
  lastMessageThreadId: string | null
  hidden: boolean
  availableChannels: ConversationChannel[]
  /** The latest customer interaction's primary intent; never request history. */
  latestIntent: InteractionIntent | null
  /** When the latest interaction happened, and what the records say of it. */
  latestInteractionAtMs?: number
  latestFacts?: ConversationFacts
  details: PatientDetails
}

export type CanonicalInteractionAggregate = Omit<
  LegacyInteractionAggregate,
  'identityBasis' | 'conversationId' | 'aggregateId' | 'patientId'
> & {
  identityBasis: 'CANONICAL_INTERACTION_AGGREGATE'
  conversationId: string
  aggregateId: string
  patientId: string | null
  identityStatus: 'LEGACY_BOUND' | 'CONFLICTED' | 'RECONCILIATION_REQUIRED'
}

export type Conversation =
  LegacyInteractionAggregate | CanonicalInteractionAggregate

export type ThreadSummary = {
  threadId: string
  startTimestamp: number
  channel: ConversationChannel
  serviceMissing: boolean
}

export type ConversationMessage = {
  messageId: string
  timestamp: number
  content: string
  author: MessageAuthor
  channel: ConversationChannel
  deliveryFailed: boolean
  deliveryState: 'PENDING' | 'SENT' | 'FAILED' | 'UNCONFIRMED' | null
}

function insuranceText(value: unknown): string | null {
  if (typeof value === 'string') return cleanText(value)
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null
  }
  const record = value as Record<string, unknown>
  return (
    cleanText(record.name) ??
    cleanText(record.insurance) ??
    cleanText(record.provider)
  )
}

export function normalizeChannel(
  service: unknown,
  source?: unknown
): ConversationChannel {
  const normalizedService = cleanText(service)?.toLowerCase()
  const normalizedSource = cleanText(source)?.toLowerCase()
  if (
    normalizedService === 'webwidget' ||
    normalizedService === 'widget' ||
    normalizedSource === 'widget'
  ) {
    return 'Web Widget'
  }
  if (
    normalizedService === 'call' ||
    normalizedService === 'voicecall' ||
    normalizedSource === 'voice call'
  ) {
    return 'Voice'
  }
  if (normalizedService === 'sms' || normalizedSource === 'sms') return 'SMS'
  return 'Unknown'
}

export function normalizePatient(
  patientId: string,
  value: unknown
): Conversation | null {
  const parsed = patientSchema.safeParse(value)
  if (!parsed.success) return null
  const patient = parsed.data
  const info = patient.info
  const combinedName = [cleanText(info?.firstName), cleanText(info?.lastName)]
    .filter(Boolean)
    .join(' ')
  const displayName =
    cleanText(info?.name) ?? cleanText(combinedName) ?? 'Patient'
  const recordedRequests: PatientDetails['recordedRequests'] = []
  if (info?.apptReq) recordedRequests.push('Appointment')
  if (info?.callReq) recordedRequests.push('Callback')
  if (info?.cancelReq) recordedRequests.push('Cancellation')
  if (info?.resReq) recordedRequests.push('Reschedule')

  return {
    identityBasis: 'LEGACY_PATIENT_KEY',
    conversationId: patientId,
    aggregateId: null,
    patientId,
    timestamp: patient.lastMessage.timestamp,
    preview: cleanText(patient.lastMessage.content) ?? 'No message preview',
    channel: normalizeChannel(
      patient.lastMessage.service,
      patient.lastMessage.source ?? info?.source
    ),
    lastMessageCallId: cleanText(patient.lastMessage.callId),
    lastMessageThreadId: cleanText(patient.lastMessage.threadID),
    hidden: patient.trash === true || patient.archive === true,
    availableChannels: [
      normalizeChannel(
        patient.lastMessage.service,
        patient.lastMessage.source ?? info?.source
      ),
    ],
    latestIntent: null,
    details: {
      displayName,
      associatedPatientNames: info?.associatedPatientNames,
      phone: phoneNumber(info?.phoneNumber) ?? phoneNumber(patientId),
      dateOfBirth:
        cleanText(info?.birthday) ??
        cleanText(info?.dob) ??
        cleanText(info?.dateOfBirth),
      email: cleanText(info?.email),
      insurance: insuranceText(info?.patientInsuranceInfo),
      procedure: cleanText(info?.procedure),
      patientStatus: cleanText(info?.patientStatus),
      source: cleanText(info?.source),
      aiMode: typeof info?.AIMode === 'boolean' ? info.AIMode : null,
      favorite: info?.favorite === true,
      isEmergency: info?.isEmergency === true,
      appointmentBooked: info?.apptBooked === true,
      recordedRequests,
    },
  }
}

export function normalizeInteractionAggregate(
  aggregateId: string,
  value: unknown
): Conversation | null {
  const parsed = interactionAggregateRecordSchema.safeParse(value)
  if (!parsed.success || parsed.data.aggregateId !== aggregateId) return null
  const aggregate = parsed.data
  const channel =
    aggregate.display.latestChannel === 'VOICE'
      ? 'Voice'
      : aggregate.display.latestChannel === 'SMS'
        ? 'SMS'
        : aggregate.display.latestChannel === 'WEB_WIDGET'
          ? 'Web Widget'
          : 'Unknown'
  return {
    identityBasis: 'CANONICAL_INTERACTION_AGGREGATE',
    conversationId: aggregateId,
    aggregateId,
    patientId: null,
    identityStatus: aggregate.identity.status,
    timestamp: aggregate.display.latestActivityAtMs,
    preview: aggregate.display.latestPreview,
    channel,
    lastMessageCallId: null,
    lastMessageThreadId: null,
    hidden: aggregate.visibility === 'HIDDEN',
    availableChannels: [
      ...(aggregate.channels.hasSms ? (['SMS'] as const) : []),
      ...(aggregate.channels.hasVoice ? (['Voice'] as const) : []),
      ...(aggregate.channels.hasWidget ? (['Web Widget'] as const) : []),
    ],
    latestIntent: aggregate.latestInteraction?.intent ?? null,
    latestInteractionAtMs: aggregate.latestInteraction?.atMs,
    latestFacts: aggregate.latestInteraction
      ? {
          intent: aggregate.latestInteraction.intent,
          secondary: aggregate.latestInteraction.secondary,
          handoff: aggregate.latestInteraction.handoff,
          information: aggregate.latestInteraction.information,
          requestPassed: aggregate.latestInteraction.requestPassed,
          need: aggregate.latestInteraction.need,
          topics: aggregate.latestInteraction.topics,
        }
      : undefined,
    details: {
      displayName: aggregate.display.displayName,
      associatedPatientNames: aggregate.associations?.patients.map(
        (patient) => patient.displayName
      ),
      phone: aggregate.display.phoneDisplay,
      dateOfBirth: null,
      email: null,
      insurance: null,
      procedure: null,
      patientStatus: null,
      source: null,
      aiMode: null,
      favorite: false,
      isEmergency: false,
      appointmentBooked: false,
      recordedRequests: [],
    },
  }
}

export function normalizeThreads(value: unknown): ThreadSummary[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return []
  }
  return Object.entries(value as Record<string, unknown>)
    .flatMap(([threadId, raw]) => {
      const parsed = threadSchema.safeParse(raw)
      if (!parsed.success || !threadId.trim()) return []
      const service = cleanText(parsed.data.service)
      const source = cleanText(parsed.data.source)
      const channel = normalizeChannel(service, source)
      return [
        {
          threadId,
          startTimestamp: parsed.data.startChatTs,
          channel,
          serviceMissing: service === null,
        } satisfies ThreadSummary,
      ]
    })
    .sort(
      (left, right) =>
        left.startTimestamp - right.startTimestamp ||
        left.threadId.localeCompare(right.threadId)
    )
}

export function validThread(value: unknown): boolean {
  return threadSchema.safeParse(value).success
}

export function exactCallThreadId(value: unknown): string | null {
  const parsed = callSchema.safeParse(value)
  return parsed.success && parsed.data.threadOwnership
    ? cleanText(parsed.data.threadOwnership.threadID)
    : null
}

function messageAuthor(receive: unknown, sender: unknown) {
  if (receive === true) return 'Patient' as const
  if (receive !== false) return 'Unknown' as const
  const normalizedSender = cleanText(sender)?.toLowerCase()
  if (normalizedSender === 'ai') return 'AI' as const
  if (normalizedSender === 'me' || normalizedSender === 'staff') {
    return 'Staff' as const
  }
  if (normalizedSender) return 'Clinic' as const
  return 'Outbound unknown' as const
}

export function normalizeMessage(
  messageId: string,
  value: unknown,
  threadChannel: ConversationChannel
): ConversationMessage | null {
  const parsed = messageSchema.safeParse(value)
  if (!parsed.success || !messageId.trim()) return null
  const content = cleanText(parsed.data.content)
  if (!content) return null
  const ownChannel = normalizeChannel(parsed.data.service, parsed.data.source)
  // A text whose provider outcome Flio couldn't confirm keeps deliveryState
  // RECONCILIATION_REQUIRED, beside the failure status -1, until an outcome is
  // found, and for good if none is. It may have been delivered, so it reads as
  // unconfirmed, never as failed (Centereach, 2026-10-30).
  const unconfirmed =
    parsed.data.status !== 1 &&
    parsed.data.deliveryState === 'RECONCILIATION_REQUIRED'
  return {
    messageId,
    timestamp: parsed.data.timestamp,
    content,
    author: messageAuthor(parsed.data.receive, parsed.data.sender),
    channel: ownChannel === 'Unknown' ? threadChannel : ownChannel,
    deliveryFailed: parsed.data.status === -1 && !unconfirmed,
    deliveryState: unconfirmed
      ? 'UNCONFIRMED'
      : parsed.data.status === 0
        ? 'PENDING'
        : parsed.data.status === 1
          ? 'SENT'
          : parsed.data.status === -1
            ? 'FAILED'
            : null,
  }
}
